import React from 'react';
import { FaInstagram, FaTiktok } from 'react-icons/fa';
import { DeliverableLink, isVoided } from '@foodfluence/shared';
import deliverableNames from 'utils/deliverableNames';
import { linkNeedsEdits } from 'utils/influencerCollabPhase';
import StatusCard from './StatusCard';
import { deliverableCardStatus } from '../utils/submissionCards';

/** `deliverableName` names the platform in the action labels, e.g. "Instagram Reel". */
function getSubLabel(status: string, deliverableName: string, voided: boolean, needsEdits: boolean): string {
  // A voided slot keeps its original `status`, so the void has to win over the status copy here.
  if (voided) return 'Removed by the brand — no longer needed';
  if (needsEdits) return 'Needs edits';
  switch (status) {
    case 'approved':
      return 'Completed';
    case 'pendingDraftReview':
      return 'Under review';
    case 'pendingVerification':
      return 'Pending verification';
    case 'pendingDraftSubmission':
      return `Submit ${deliverableName} draft`;
    case 'pendingSubmission':
    default:
      return `Submit ${deliverableName} link`;
  }
}

interface Props {
  deliverableKey: string;
  index: number;
  link: DeliverableLink;
  onClick: () => void;
  /** Names who asked for edits on the inline feedback note. */
  brandName?: string;
}

const DeliverableCard: React.FC<Props> = ({ deliverableKey, index, link, onClick, brandName }) => {
  const PlatformIcon = deliverableKey === 'tiktoks' ? FaTiktok : FaInstagram;
  const name = deliverableNames[deliverableKey as keyof typeof deliverableNames]?.singular ?? deliverableKey;
  const voided = isVoided(link);
  const needsEdits = !voided && linkNeedsEdits(link);
  const status = deliverableCardStatus(link.status, { voided, needsEdits, isStory: deliverableKey === 'instagramStories' });
  // Draft edits come from the brand; a rejected live link (pendingSubmission + reason) from staff review.
  const feedback = needsEdits ? (link.feedback || link.rejectionReason) : null;
  const feedbackFrom = link.status === 'needsInfluencerEdit' ? brandName : 'FoodFluence';

  return (
    <StatusCard
      icon={PlatformIcon}
      title={`${name} #${index + 1}`}
      sub={getSubLabel(link.status, name, voided, needsEdits)}
      status={status}
      feedback={feedback}
      feedbackFrom={feedbackFrom}
      onClick={onClick}
      isInactive={voided}
    />
  );
};

export default React.memo(DeliverableCard);
